import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Inline magic-byte sniffer that mirrors the server-side
 * `services/file_validation.detect_document_kind()`. We read only the first
 * 8 bytes (more than enough for PDF / DOCX / DOC headers) so even huge files
 * stay snappy.
 *
 * Returns: 'pdf' | 'docx' | 'doc' | 'txt' | 'csv' | 'md' | null
 */
const detectKind = async (file) => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const slice = file.slice(0, 16);
  const buf = new Uint8Array(await slice.arrayBuffer());
  const hex = (n) => buf[n]?.toString(16).padStart(2, '0');

  // PDF: %PDF-
  if (buf[0] === 0x25 && buf[1] === 0x50 && buf[2] === 0x44 && buf[3] === 0x46 && buf[4] === 0x2d) {
    return 'pdf';
  }
  // ZIP container (DOCX/XLSX/PPTX) — PK\x03\x04
  if (buf[0] === 0x50 && buf[1] === 0x4b && (buf[2] === 0x03 || buf[2] === 0x05 || buf[2] === 0x07)) {
    if (['docx', 'xlsx', 'pptx'].includes(ext)) return ext;
    return null;
  }
  // OLE2 (legacy .doc / .xls) — D0 CF 11 E0 A1 B1 1A E1
  if (
    hex(0) === 'd0' && hex(1) === 'cf' && hex(2) === '11' && hex(3) === 'e0' &&
    hex(4) === 'a1' && hex(5) === 'b1' && hex(6) === '1a' && hex(7) === 'e1'
  ) {
    return 'doc';
  }
  // Plain text formats — check that the first 4KB is UTF-8/ASCII-ish.
  if (['txt', 'md', 'csv'].includes(ext)) {
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(buf);
      // Count control chars (excluding tab/newline/CR/esc).
      let bad = 0;
      for (const ch of text) {
        const code = ch.charCodeAt(0);
        if (code < 9 || (code > 13 && code < 32 && code !== 27)) bad++;
      }
      if (bad > 3) return null;
      return ext;
    } catch {
      return null;
    }
  }
  return null;
};

const PRETTY = {
  pdf: 'PDF document',
  docx: 'Word document (DOCX)',
  doc: 'Word document (legacy DOC)',
  xlsx: 'Excel spreadsheet',
  pptx: 'PowerPoint deck',
  txt: 'Plain text',
  md: 'Markdown',
  csv: 'CSV spreadsheet',
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Drop-zone for knowledge base uploads. Live MIME-type feedback echoes the
 * server's magic-byte validation so users see "✓ Detected PDF" before they
 * even click upload.
 *
 * Props:
 *  - onFileAccepted(file): callback when a file passes client-side validation
 *  - uploading: boolean — parent controls the in-flight state
 *  - acceptedExts: array of allowed extensions (without dot)
 */
export const KnowledgeDropzone = ({
  onFileAccepted,
  uploading = false,
  acceptedExts = ['pdf', 'txt', 'docx', 'md', 'csv'],
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [detected, setDetected] = useState(null); // {kind, name, size} | null
  const [errorMsg, setErrorMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const acceptAttr = acceptedExts.map((e) => `.${e}`).join(',');

  const validate = useCallback(async (file) => {
    setErrorMsg('');
    setDetected(null);
    if (!file) return;

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!acceptedExts.includes(ext)) {
      setErrorMsg(`Unsupported extension .${ext}. Allowed: ${acceptedExts.map((e) => '.' + e).join(', ')}`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMsg('File too large (max 10 MB).');
      return;
    }
    if (file.size < 4) {
      setErrorMsg('File appears to be empty.');
      return;
    }

    setBusy(true);
    try {
      const kind = await detectKind(file);
      if (!kind) {
        setErrorMsg(`Contents don't match a valid .${ext} file. The server will reject this upload.`);
        return;
      }
      if (kind !== ext && !(kind === 'txt' && ['md', 'csv'].includes(ext))) {
        setErrorMsg(`Contents look like .${kind} but filename says .${ext}. Rename the file or pick another.`);
        return;
      }
      setDetected({ kind, name: file.name, size: file.size });
      onFileAccepted?.(file);
    } finally {
      setBusy(false);
    }
  }, [acceptedExts, onFileAccepted]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    // Iterate so multi-file drops are validated + uploaded one by one.
    files.forEach((file) => validate(file));
  }, [validate]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) setDragActive(true);
  }, [dragActive]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleClick = () => inputRef.current?.click();
  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => validate(file));
    // Reset so re-selecting the same file fires the change event again.
    if (inputRef.current) inputRef.current.value = '';
  };

  const reset = () => { setDetected(null); setErrorMsg(''); };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
      data-testid="knowledge-dropzone"
      className={cn(
        'group relative cursor-pointer rounded-xl border-2 border-dashed p-6 transition-all',
        dragActive
          ? 'border-blue-500 bg-blue-50/70'
          : errorMsg
          ? 'border-red-300 bg-red-50/40'
          : detected
          ? 'border-emerald-300 bg-emerald-50/60'
          : 'border-slate-300 bg-slate-50/60 hover:border-blue-400 hover:bg-blue-50/40',
        uploading && 'opacity-60 pointer-events-none',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={acceptAttr}
        className="hidden"
        onChange={handleChange}
        data-testid="knowledge-dropzone-input"
        aria-label="Upload knowledge document"
      />

      <div className="flex items-start gap-4">
        <div className={cn(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg transition-colors',
          dragActive ? 'bg-blue-500 text-white'
            : errorMsg ? 'bg-red-100 text-red-600'
            : detected ? 'bg-emerald-100 text-emerald-700'
            : 'bg-white border border-slate-200 text-slate-500 group-hover:text-blue-600',
        )}>
          {uploading || busy ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : detected ? (
            <CheckCircle2 className="h-6 w-6" />
          ) : errorMsg ? (
            <AlertCircle className="h-6 w-6" />
          ) : (
            <Upload className="h-6 w-6" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {detected ? (
            <div data-testid="knowledge-dropzone-detected">
              <p className="text-sm font-medium text-emerald-900 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />
                Detected {PRETTY[detected.kind] || detected.kind.toUpperCase()}
              </p>
              <p className="text-xs text-emerald-700 mt-0.5 truncate">
                <FileText className="inline h-3.5 w-3.5 mr-1" />
                {detected.name} <span className="text-emerald-500">· {formatSize(detected.size)}</span>
              </p>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); reset(); }}
                className="text-xs text-emerald-700 underline mt-1 hover:text-emerald-900"
              >
                Pick a different file
              </button>
            </div>
          ) : errorMsg ? (
            <div data-testid="knowledge-dropzone-error">
              <p className="text-sm font-medium text-red-700 flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4" />
                File rejected
              </p>
              <p className="text-xs text-red-600 mt-0.5">{errorMsg}</p>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); reset(); }}
                className="text-xs text-red-700 underline mt-1 hover:text-red-900"
              >
                Try another file
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-slate-900">
                {dragActive ? 'Drop your files to upload' : 'Drag & drop files here, or click to browse'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {acceptedExts.map((e) => '.' + e).join(', ')} · up to 10&nbsp;MB each · multiple files supported
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeDropzone;
